import {
  FIT_WIDTH_RATIO,
  LARGE_FILE_BYTES,
  MAX_CACHED_FRAMES,
  PREFETCH_AHEAD,
  PREFETCH_BEHIND,
  STAGE_MAX_HEIGHT,
  STAGE_MAX_WIDTH,
} from './constants'

export const rasterizeBitmap = (bitmap: ImageBitmap): HTMLCanvasElement => {
  const canvas = document.createElement('canvas')
  canvas.width = bitmap.width
  canvas.height = bitmap.height
  const context = canvas.getContext('2d')
  try {
    if (!context) throw new Error('canvas')
    context.drawImage(bitmap, 0, 0)
  } finally {
    bitmap.close()
  }
  return canvas
}

export type CacheEntry = { key: number; bytes: number }

export const evictToBudget = (
  entries: CacheEntry[],
  rank: (key: number) => number,
  budget: number,
  maxCount = MAX_CACHED_FRAMES
): CacheEntry[] => {
  const ordered = entries
    .map(entry => ({ entry, rank: rank(entry.key) }))
    .filter(item => Number.isFinite(item.rank))
    .sort((a, b) => a.rank - b.rank)
  const kept: CacheEntry[] = []
  let used = 0
  for (const { entry } of ordered) {
    if (kept.length >= maxCount) break
    if (used + entry.bytes > budget) {
      if (kept.length === 0) kept.push(entry)
      break
    }
    kept.push(entry)
    used += entry.bytes
  }
  return kept
}

export type PlayTick = { action: 'advance'; cursor: number } | { action: 'stop' }

export const playTick = (cursor: number, length: number): PlayTick => {
  if (length <= 0 || cursor >= length - 1) return { action: 'stop' }
  return { action: 'advance', cursor: cursor + 1 }
}

export const frameWindow = (cursor: number, length: number, ahead: number, behind: number): number[] => {
  if (cursor < 0 || cursor >= length) return []
  const positions = [cursor]
  for (let offset = 1; offset <= Math.max(ahead, behind); offset++) {
    if (offset <= ahead && cursor + offset < length) positions.push(cursor + offset)
    if (offset <= behind && cursor - offset >= 0) positions.push(cursor - offset)
  }
  return positions
}

export const windowRank = (visible: number[], cursor: number, featureIndex: number): number => {
  const rank = frameWindow(cursor, visible.length, PREFETCH_AHEAD, PREFETCH_BEHIND).findIndex(
    position => visible[position] === featureIndex
  )
  return rank < 0 ? Infinity : rank
}

export const orderByOffset = <T extends { offset: number }>(items: T[]): T[] =>
  [...items].sort((a, b) => a.offset - b.offset)

export type ImageSize = { width: number; height: number }
export type BitmapResize = { resizeWidth: number; resizeHeight?: number }

export const fitResize = (size: ImageSize | null, box: ImageSize): BitmapResize | undefined => {
  if (!size) return { resizeWidth: Math.max(1, Math.round(box.width)) }
  if (size.width < 1 || size.height < 1) return undefined
  const scale = Math.min(box.width / size.width, box.height / size.height, 1)
  if (scale >= 1) return undefined
  return {
    resizeWidth: Math.max(1, Math.round(size.width * scale)),
    resizeHeight: Math.max(1, Math.round(size.height * scale)),
  }
}

export const decodeStageImage = async (blob: Blob, stage: HTMLElement | null): Promise<HTMLCanvasElement> => {
  const ratio = window.devicePixelRatio || 1
  const box = {
    width: Math.min(STAGE_MAX_WIDTH, Math.round((stage?.clientWidth || 1280) * ratio * FIT_WIDTH_RATIO)),
    height: Math.min(STAGE_MAX_HEIGHT, Math.round((stage?.clientHeight || 720) * ratio)),
  }
  const resize = fitResize(await imageDimensions(blob), box)
  const bitmap = await createImageBitmap(blob, resize ? { ...resize, resizeQuality: 'medium' } : undefined)
  return rasterizeBitmap(bitmap)
}

export const MAX_LIGHTBOX_EDGE = 4096

export const lightboxResize = (size: ImageSize | null): BitmapResize | undefined =>
  fitResize(size, { width: MAX_LIGHTBOX_EDGE, height: MAX_LIGHTBOX_EDGE })

const HEADER_CHUNK = 65536
const MAX_JPEG_SCAN = 16 * 1024 * 1024

class BlobWindow {
  private start = 0
  private bytes = new Uint8Array(0)

  constructor(private readonly blob: Blob) {}

  async read(offset: number, length: number): Promise<Uint8Array | null> {
    if (offset < 0 || offset + length > this.blob.size) return null
    if (offset < this.start || offset + length > this.start + this.bytes.length) {
      this.start = offset
      this.bytes = new Uint8Array(await this.blob.slice(offset, offset + Math.max(length, HEADER_CHUNK)).arrayBuffer())
    }
    return this.bytes.subarray(offset - this.start, offset - this.start + length)
  }
}

const isSofMarker = (marker: number) =>
  marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc

const isStandaloneMarker = (marker: number) => marker === 0x01 || (marker >= 0xd0 && marker <= 0xd9)

const jpegDimensions = async (window: BlobWindow): Promise<ImageSize | null> => {
  let offset = 2
  while (offset < MAX_JPEG_SCAN) {
    const head = await window.read(offset, 4)
    if (!head) return null
    if (head[0] !== 0xff) return null
    const marker = head[1]
    if (marker === 0xff) {
      offset += 1
      continue
    }
    if (isStandaloneMarker(marker)) {
      offset += 2
      continue
    }
    if (marker === 0xda) return null
    const length = (head[2] << 8) | head[3]
    if (length < 2) return null
    if (isSofMarker(marker)) {
      const frame = await window.read(offset + 5, 4)
      if (!frame) return null
      const height = (frame[0] << 8) | frame[1]
      const width = (frame[2] << 8) | frame[3]
      return width && height ? { width, height } : null
    }
    offset += 2 + length
  }
  return null
}

const ascii = (bytes: Uint8Array, start: number, length: number) =>
  String.fromCharCode(...bytes.subarray(start, start + length))

const webpDimensions = (bytes: Uint8Array): ImageSize | null => {
  if (bytes.length < 30 || ascii(bytes, 0, 4) !== 'RIFF' || ascii(bytes, 8, 4) !== 'WEBP') return null
  const chunk = ascii(bytes, 12, 4)
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  if (chunk === 'VP8 ') {
    return { width: view.getUint16(26, true) & 0x3fff, height: view.getUint16(28, true) & 0x3fff }
  }
  if (chunk === 'VP8L') {
    const b0 = bytes[21]
    const b1 = bytes[22]
    const b2 = bytes[23]
    const b3 = bytes[24]
    return {
      width: 1 + (((b1 & 0x3f) << 8) | b0),
      height: 1 + (((b3 & 0x0f) << 10) | (b2 << 2) | ((b1 & 0xc0) >> 6)),
    }
  }
  if (chunk === 'VP8X') {
    return {
      width: 1 + (bytes[24] | (bytes[25] << 8) | (bytes[26] << 16)),
      height: 1 + (bytes[27] | (bytes[28] << 8) | (bytes[29] << 16)),
    }
  }
  return null
}

export const imageDimensions = async (blob: Blob): Promise<ImageSize | null> => {
  const window = new BlobWindow(blob)
  const bytes = await window.read(0, Math.min(blob.size, 30))
  if (!bytes || bytes.length < 4) return null
  if (bytes.length >= 24 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
    return { width: view.getUint32(16), height: view.getUint32(20) }
  }
  if (bytes[0] === 0xff && bytes[1] === 0xd8) return jpegDimensions(window)
  return webpDimensions(bytes)
}

export const needsLargeFileConfirm = (size: number): boolean => size >= LARGE_FILE_BYTES

export const formatBytes = (size: number): string => {
  const gib = size / (1024 * 1024 * 1024)
  if (gib >= 1) return `${gib >= 10 ? Math.round(gib) : gib.toFixed(1)} GB`
  const mib = size / (1024 * 1024)
  return `${Math.max(1, Math.round(mib))} MB`
}
